import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useClubScope } from "../../contexts/ClubScopeContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useSystemButtonStyles } from "../../hooks/useSystemButtonStyles";
import { PAGE_MAX_WIDTH, pageContainerSx } from "../../constants/layout";
import {
  fetchPosProducts,
  fetchPosStorefront,
  verifyPosCheckout,
  fetchPosReceipt,
  cancelPosCheckout,
} from "../../utils/api/api";
import { savePosShopCheckout } from "./posShopCheckoutStorage";
import PosShopReceipt from "./PosShopReceipt";
import { ThemeProvider as CustomThemeProvider } from "../../contexts/ThemeContext";
import { getMm, getPageTitleSx } from "../../theme/modernMaterialEnterprise";

const NUNITO = "'Plus Jakarta Sans', sans-serif";

function formatPrice(cents, currency = "SEK") {
  const n = Number(cents);
  if (Number.isNaN(n)) return "—";
  try {
    return new Intl.NumberFormat("sv-SE", { style: "currency", currency }).format(n / 100);
  } catch {
    return `${(n / 100).toFixed(2)} ${currency}`;
  }
}

function isOutOfStock(product) {
  return product.trackInventory && product.stockQuantity != null && product.stockQuantity <= 0;
}

function maxQty(product, currentInCart) {
  if (!product.trackInventory || product.stockQuantity == null) return 99;
  return Math.max(0, product.stockQuantity - (currentInCart || 0));
}

function productImageUrls(product) {
  const urls = [];
  if (product?.primaryImageUrl) urls.push(product.primaryImageUrl);
  (product?.images || []).forEach((img) => {
    const url = typeof img === "string" ? img : img?.url;
    if (url && !urls.includes(url)) urls.push(url);
  });
  return urls;
}

function PosShopProductDetailDialog({
  product,
  open,
  onClose,
  isDarkMode,
  currency,
  inCart,
  onBuyNow,
  onAddToCart,
  buttonSx,
  t,
}) {
  const { toolbarButtonSx } = useSystemButtonStyles();
  if (!product) return null;

  const out = isOutOfStock(product);
  const images = productImageUrls(product);
  const detailText = product.description?.trim() || product.shortDescription?.trim() || "";
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;
  const border = `1px solid ${getMm(isDarkMode).outlineVariant}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "8px",
          bgcolor: getMm(isDarkMode).surfaceLowest,
          border,
          fontFamily: NUNITO,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          color: text,
          fontFamily: NUNITO,
          fontWeight: 700,
          fontSize: "1.25rem",
        }}
      >
        <span>{product.name}</span>
        <IconButton onClick={onClose} size="small" aria-label={t("close")} sx={{ color: muted, mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {images.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Box
              component="img"
              src={images[0]}
              alt=""
              sx={{
                width: "100%",
                maxHeight: 320,
                objectFit: "cover",
                borderRadius: "8px",
                display: "block",
                border,
              }}
            />
            {images.length > 1 && (
              <Box sx={{ display: "flex", gap: 1, mt: 1, overflowX: "auto", pb: 0.5 }}>
                {images.slice(1).map((url) => (
                  <Box
                    key={url}
                    component="img"
                    src={url}
                    alt=""
                    sx={{
                      width: 72,
                      height: 72,
                      objectFit: "cover",
                      borderRadius: "8px",
                      flexShrink: 0,
                      border,
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ height: 160, bgcolor: getMm(isDarkMode).surfaceContainer, borderRadius: "8px", mb: 2 }} />
        )}

        <Typography sx={{ fontWeight: 800, fontSize: "1.375rem", color: text, mb: 1.5, fontFamily: NUNITO }}>
          {formatPrice(product.priceCents, product.currency || currency)}
        </Typography>

        {product.shortDescription?.trim() && product.description?.trim() && product.shortDescription.trim() !== product.description.trim() && (
          <Typography sx={{ fontSize: "0.9375rem", color: muted, mb: 1.5, fontFamily: NUNITO }}>
            {product.shortDescription}
          </Typography>
        )}

        {detailText ? (
          <Typography
            sx={{
              fontSize: "0.9375rem",
              color: getMm(isDarkMode).onSurfaceVariant,
              whiteSpace: "pre-wrap",
              lineHeight: 1.6,
              fontFamily: NUNITO,
            }}
          >
            {product.description?.trim() || product.shortDescription}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: "0.9375rem", color: muted, fontFamily: NUNITO }}>
            {t("pos_shop_no_description")}
          </Typography>
        )}

        {out && (
          <Typography sx={{ fontSize: "0.875rem", color: "#EF4444", mt: 2, fontFamily: NUNITO }}>
            {t("pos_shop_out_of_stock")}
          </Typography>
        )}
        {!out && product.trackInventory && product.stockQuantity != null && product.stockQuantity <= 5 && (
          <Typography sx={{ fontSize: "0.875rem", color: muted, mt: 2, fontFamily: NUNITO }}>
            {t("pos_shop_low_stock", { count: product.stockQuantity })}
          </Typography>
        )}
        {inCart > 0 && !out && (
          <Typography sx={{ fontSize: "0.875rem", color: muted, mt: 2, fontFamily: NUNITO }}>
            {t("pos_shop_in_cart", { count: inCart })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, flexDirection: "column", gap: 1, alignItems: "stretch" }}>
        <Button variant="contained" color="primary" fullWidth disabled={out} onClick={() => onBuyNow(product)} sx={{ ...buttonSx, py: 0.85 }}>
          {t("pos_shop_buy_now")}
        </Button>
        <Button variant="outlined"
          fullWidth
          size="small"
         
          startIcon={<AddIcon />}
          disabled={out || maxQty(product, inCart) <= 0}
          onClick={() => onAddToCart(product)}
          sx={toolbarButtonSx}
        >
          {inCart > 0 ? t("pos_shop_add_more") : t("pos_shop_add_to_cart")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PosShopPage({ selectedPage, setSelectedPage }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const {
    primaryButtonSx,
    cancelButtonSx,
    outlineButtonSx,
    secondaryButtonSx,
    destructiveButtonSx,
    destructiveOutlineButtonSx,
    greenSmileyButtonSx,
    redSmileyButtonSx,
    iconActionSx,
    iconActionTableSx,
    iconActionLargeSx,
    toolbarButtonSx,
    dialogPrimarySx,
    dialogCancelSx,
    dialogFormPrimarySx,
    dialogFormCancelSx,
    dialogFormDestructiveSx,
    warningButtonSx,
  } = useSystemButtonStyles();
  const buttonSx = primaryButtonSx;
  const { clubID } = useClubScope();
  const [loading, setLoading] = useState(true);
  const [storefront, setStorefront] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [detailProduct, setDetailProduct] = useState(null);

  const cardSx = useMemo(
    () => ({
      height: "100%",
      backgroundColor: getMm(isDarkMode).surfaceLowest,
      border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
      borderRadius: "8px",
      boxShadow: "none",
      fontFamily: NUNITO,
      overflow: "hidden",
    }),
    [isDarkMode]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sf, items] = await Promise.all([
        fetchPosStorefront(clubID),
        fetchPosProducts({ admin: false, clubID }),
      ]);
      setStorefront(sf);
      setProducts(Array.isArray(items) ? items : []);
    } finally {
      setLoading(false);
    }
  }, [clubID]);

  useEffect(() => {
    if (selectedPage === "PosShopPage") load();
  }, [selectedPage, load]);

  const productMap = useMemo(() => {
    const map = new Map();
    products.forEach((p) => map.set(p.id, p));
    return map;
  }, [products]);

  const cartLines = useMemo(() => {
    return Object.entries(cart)
      .filter(([, qty]) => qty > 0)
      .map(([productId, quantity]) => {
        const product = productMap.get(productId);
        if (!product) return null;
        return { product, quantity };
      })
      .filter(Boolean);
  }, [cart, productMap]);

  const cartTotalCents = useMemo(
    () => cartLines.reduce((sum, line) => sum + line.product.priceCents * line.quantity, 0),
    [cartLines]
  );

  const cartCount = useMemo(
    () => cartLines.reduce((sum, line) => sum + line.quantity, 0),
    [cartLines]
  );

  const openProductDetail = (product) => setDetailProduct(product);
  const closeProductDetail = () => setDetailProduct(null);

  const addToCart = (product) => {
    if (isOutOfStock(product)) return;
    setCart((prev) => {
      const current = prev[product.id] || 0;
      const cap = product.trackInventory && product.stockQuantity != null ? product.stockQuantity : 99;
      if (current >= cap) return prev;
      return { ...prev, [product.id]: current + 1 };
    });
  };

  const setLineQty = (productId, quantity) => {
    const product = productMap.get(productId);
    if (!product) return;
    const cap = product.trackInventory && product.stockQuantity != null ? product.stockQuantity : 99;
    const qty = Math.max(0, Math.min(cap, Math.round(quantity)));
    setCart((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[productId];
      else next[productId] = qty;
      return next;
    });
  };

  const goToCheckout = (items) => {
    if (!items?.length || !setSelectedPage) return;
    savePosShopCheckout(items);
    setSelectedPage("PosShopCheckoutPage");
  };

  const handleContinueCheckout = () => {
    if (cartLines.length === 0) return;
    goToCheckout(cartLines.map((line) => ({ productId: line.product.id, quantity: line.quantity })));
  };

  const handleBuyNow = (product) => {
    if (isOutOfStock(product)) return;
    goToCheckout([{ productId: product.id, quantity: 1 }]);
  };

  if (selectedPage !== "PosShopPage") return null;

  if (loading) {
    return (
      <Box sx={{ ...pageContainerSx, display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
      </Box>
    );
  }

  if (!storefront?.visibleInMenu) {
    return (
      <Box sx={{ ...pageContainerSx, p: 3, maxWidth: PAGE_MAX_WIDTH, mx: "auto", fontFamily: NUNITO }}>
        <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant }}>{t("pos_shop_unavailable")}</Typography>
      </Box>
    );
  }

  const currency = storefront?.currency || "SEK";

  return (
    <Box sx={{ ...pageContainerSx, p: { xs: 2, sm: 3 }, pb: { xs: 14, sm: 3 }, maxWidth: PAGE_MAX_WIDTH, mx: "auto", fontFamily: NUNITO, position: "relative" }}>
      <Box sx={{ position: "absolute", top: { xs: 16, sm: 24 }, right: { xs: 8, sm: 24 }, zIndex: 10 }}>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <StorefrontIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontSize: 32 }} />
        <Typography sx={{ ...getPageTitleSx(isDarkMode) }}>
          {t("pos_shop_title")}
        </Typography>
      </Box>
      <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant, mb: 3, maxWidth: 560 }}>
        {t("pos_shop_intro_checkout")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={cartLines.length > 0 ? 8 : 12}>
          {products.length === 0 ? (
            <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant }}>{t("pos_shop_empty")}</Typography>
          ) : (
            <Grid container spacing={2}>
              {products.map((p) => {
                const out = isOutOfStock(p);
                const inCart = cart[p.id] || 0;
                return (
                  <Grid item xs={12} sm={6} md={cartLines.length > 0 ? 6 : 4} key={p.id}>
                    <Card sx={cardSx}>
                      <Box
                        onClick={() => openProductDetail(p)}
                        sx={{
                          cursor: "pointer",
                          "&:hover .pos-shop-product-media": {
                            opacity: 0.92,
                          },
                        }}
                      >
                        {p.primaryImageUrl ? (
                          <Box
                            className="pos-shop-product-media"
                            component="img"
                            src={p.primaryImageUrl}
                            alt=""
                            sx={{ width: "100%", height: 180, objectFit: "cover", display: "block", transition: "opacity 0.15s ease" }}
                          />
                        ) : (
                          <Box className="pos-shop-product-media" sx={{ height: 180, bgcolor: getMm(isDarkMode).surfaceContainer }} />
                        )}
                        <CardContent sx={{ p: 2, pb: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: getMm(isDarkMode).onSurface, mb: 0.5 }}>
                            {p.name}
                          </Typography>
                          {p.shortDescription && (
                            <Typography sx={{ fontSize: "0.875rem", color: getMm(isDarkMode).onSurfaceVariant, mb: 1, minHeight: 40 }}>
                              {p.shortDescription}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: getMm(isDarkMode).onSurfaceVariant, mb: 0.5 }}>
                            {t("pos_shop_view_details")}
                          </Typography>
                        </CardContent>
                      </Box>
                      <CardContent sx={{ p: 2, pt: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", color: getMm(isDarkMode).onSurface, mb: 1.5 }}>
                          {formatPrice(p.priceCents, p.currency)}
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Button
                            fullWidth
                            disabled={out}
                            onClick={() => handleBuyNow(p)}
                            sx={{ ...buttonSx, py: 0.85 }}
                          >
                            {t("pos_shop_buy_now")}
                          </Button>
                          <Button variant="outlined"
                            fullWidth
                            size="small"
                           
                            startIcon={<AddIcon />}
                            disabled={out || maxQty(p, inCart) <= 0}
                            onClick={() => addToCart(p)}
                            sx={toolbarButtonSx}
                          >
                            {inCart > 0 ? t("pos_shop_add_more") : t("pos_shop_add_to_cart")}
                          </Button>
                        </Box>
                        {out && (
                          <Typography sx={{ fontSize: "0.8125rem", color: "#EF4444", mt: 1 }}>{t("pos_shop_out_of_stock")}</Typography>
                        )}
                        {inCart > 0 && !out && (
                          <Typography sx={{ fontSize: "0.8125rem", color: getMm(isDarkMode).onSurfaceVariant, mt: 1 }}>
                            {t("pos_shop_in_cart", { count: inCart })}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>

        {cartLines.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                ...cardSx,
                position: { md: "sticky" },
                top: { md: 88 },
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <ShoppingCartIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", color: getMm(isDarkMode).onSurface }}>
                    {t("pos_shop_cart")} ({cartCount})
                  </Typography>
                </Box>
                {cartLines.map(({ product, quantity }) => (
                  <Box key={product.id} sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: getMm(isDarkMode).onSurface }}>
                      {product.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => setLineQty(product.id, quantity - 1)} sx={{ ...iconActionTableSx, p: '6px' }}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ minWidth: 24, textAlign: "center", fontFamily: NUNITO }}>{quantity}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => setLineQty(product.id, quantity + 1)}
                          disabled={maxQty(product, quantity) <= 0}
                          sx={{ ...iconActionTableSx, p: '6px' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontFamily: NUNITO }}>
                        {formatPrice(product.priceCents * quantity, product.currency)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                <Divider sx={{ my: 2, borderColor: getMm(isDarkMode).outlineVariant }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: getMm(isDarkMode).onSurface }}>{t("pos_shop_total")}</Typography>
                  <Typography sx={{ fontWeight: 700, color: getMm(isDarkMode).onSurface }}>
                    {formatPrice(cartTotalCents, currency)}
                  </Typography>
                </Box>
                <Button variant="outlined" fullWidth onClick={handleContinueCheckout} disabled={cartLines.length === 0} sx={buttonSx}>
                  {t("pos_shop_continue_checkout")}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {cartLines.length > 0 && (
        <Box
          sx={toolbarButtonSx}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontFamily: NUNITO, color: getMm(isDarkMode).onSurface }}>
              {formatPrice(cartTotalCents, currency)}
            </Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: getMm(isDarkMode).onSurfaceVariant }}>
              {t("pos_shop_cart")} ({cartCount})
            </Typography>
          </Box>
          <Button variant="contained" color="primary" onClick={handleContinueCheckout} sx={{ ...buttonSx, flexShrink: 0 }}>
            {t("pos_shop_continue_checkout")}
          </Button>
        </Box>
      )}

      <PosShopProductDetailDialog
        product={detailProduct}
        open={Boolean(detailProduct)}
        onClose={closeProductDetail}
        isDarkMode={isDarkMode}
        currency={currency}
        inCart={detailProduct ? cart[detailProduct.id] || 0 : 0}
        onBuyNow={(product) => {
          handleBuyNow(product);
          closeProductDetail();
        }}
        onAddToCart={addToCart}
        buttonSx={buttonSx}
        t={t}
      />

    </Box>
  );
}

export function PosShopPaymentSuccess() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { dialogFormPrimarySx } = useSystemButtonStyles();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [receiptFooter, setReceiptFooter] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setError(t("pos_shop_verify_missing"));
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const data = await verifyPosCheckout(sessionId);
        setOrder(data.order ?? data);
        setReceiptFooter(data.receiptFooter || "");
      } catch {
        try {
          const data = await fetchPosReceipt(sessionId);
          setOrder(data.order);
          setReceiptFooter(data.receiptFooter || "");
        } catch (err) {
          setError(err.message || t("pos_shop_verify_error"));
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [searchParams, t]);

  const isPaid = order?.status === "paid" || order?.status === "delivered";
  const border = getMm(isDarkMode).outlineVariant;

  return (
    <Box sx={{ ...pageContainerSx, p: 3, maxWidth: 560, mx: "auto", textAlign: "center", fontFamily: NUNITO }}>
      {loading ? (
        <CircularProgress sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
      ) : error ? (
        <>
          <Typography sx={{ color: "#EF4444", mb: 2 }}>{error}</Typography>
          <Button component={Link} to="/" variant="outlined" sx={{ textTransform: "none", fontFamily: NUNITO }}>
            {t("back_to_app")}
          </Button>
        </>
      ) : (
        <>
          {isPaid ? (
            <CheckCircleIcon sx={{ fontSize: 64, color: "#4CAF50", mb: 2 }} />
          ) : null}
          <Typography sx={{ fontWeight: 700, fontSize: "1.5rem", mb: 1, color: getMm(isDarkMode).onSurface }}>
            {isPaid ? t("pos_shop_payment_success_title") : t("pos_shop_payment_pending_title")}
          </Typography>
          <Typography sx={{ color: isDarkMode ? "#9CA3AF" : "text.secondary", mb: 3 }}>
            {isPaid ? t("pos_shop_payment_success_body") : t("pos_shop_payment_pending_body")}
          </Typography>
          {order && (
            <Box sx={{ mb: 3 }}>
              <PosShopReceipt order={order} receiptFooter={receiptFooter} isDarkMode={isDarkMode} />
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, justifyContent: "center", "@media print": { display: "none" } }}>
            <Button variant="contained" color="primary" component={Link} to="/" sx={dialogFormPrimarySx}>
              {t("back_to_app")}
            </Button>
            <Button component={Link} to="/?page=PosShopPage" variant="outlined" sx={{ textTransform: "none", fontFamily: NUNITO, borderColor: border, color: getMm(isDarkMode).onSurface }}>
              {t("pos_shop_back_to_shop")}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

function PosShopPaymentSuccessWrapped() {
  return (
    <CustomThemeProvider>
      <PosShopPaymentSuccess />
    </CustomThemeProvider>
  );
}

export function PosShopPaymentCancel() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { dialogFormPrimarySx } = useSystemButtonStyles();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(Boolean(searchParams.get("session_id")));
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setLoading(false);
      return;
    }
    cancelPosCheckout(sessionId)
      .then((data) => setOrder(data.order))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [searchParams]);

  const border = getMm(isDarkMode).outlineVariant;

  return (
    <Box sx={{ ...pageContainerSx, p: 3, maxWidth: 560, mx: "auto", textAlign: "center", fontFamily: NUNITO }}>
      {loading ? (
        <CircularProgress sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
      ) : (
        <>
          <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", mb: 1, color: getMm(isDarkMode).onSurface }}>
            {t("pos_shop_payment_cancel_title")}
          </Typography>
          <Typography sx={{ color: isDarkMode ? "#9CA3AF" : "text.secondary", mb: order ? 2 : 3 }}>
            {t("pos_shop_payment_cancel_body")}
          </Typography>
          {order && (
            <Box sx={{ mb: 3, textAlign: "left" }}>
              <PosShopReceipt order={order} isDarkMode={isDarkMode} showPrint={false} />
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, justifyContent: "center" }}>
            <Button variant="contained" color="primary" component={Link} to="/?page=PosShopPage" sx={dialogFormPrimarySx}>
              {t("pos_shop_back_to_shop")}
            </Button>
            <Button component={Link} to="/" variant="outlined" sx={{ textTransform: "none", fontFamily: NUNITO, borderColor: border, color: getMm(isDarkMode).onSurface }}>
              {t("back_to_app")}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

function PosShopPaymentCancelWrapped() {
  return (
    <CustomThemeProvider>
      <PosShopPaymentCancel />
    </CustomThemeProvider>
  );
}

export { PosShopPaymentSuccessWrapped, PosShopPaymentCancelWrapped };
